/**
 * Modeville product-card hover slideshow.
 *
 * Works on two card types:
 *  - Horizon <product-card> (collection pages, search; snippets/card-gallery.liquid renders up to three photos)
 *  - Modeville homepage cards (.mv-card, snippets/mv-product-card.liquid renders up to three photos)
 *
 * While a mouse pointer is over a card, the visible photo changes every 2 seconds, one at a time, looping.
 * As soon as the pointer leaves, the cycle stops and the card goes back to its first photo.
 * - Cards with fewer than 2 suitable photos stay static.
 * - Touch devices don't hover: Horizon cards keep their native swipe; homepage cards stay on one photo.
 * - Skipped entirely when the visitor prefers reduced motion.
 * - Only changes which photo is visible: the card link is never touched.
 */
(() => {
  if (window.__mvCardSlideshow) return;
  window.__mvCardSlideshow = true;

  const INTERVAL_MS = 2000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {WeakMap<Element, number>} */
  const timers = new WeakMap();

  /** @param {Element} card */
  const isHomeCard = (card) => card.classList.contains('mv-card');

  /** @param {Element} card */
  const slideshowOf = (card) => /** @type {any} */ (card.querySelector('.card-gallery slideshow-component'));

  /** @param {Element} card */
  const homeImages = (card) => Array.from(card.querySelectorAll('.mv-card__img'));

  /**
   * @param {Element} card
   * @param {number} index
   */
  const showHomeImage = (card, index) => {
    homeImages(card).forEach((img, i) => img.classList.toggle('is-active', i === index));
  };

  /** @param {Event} event */
  const cardFrom = (event) => {
    const pointerType = /** @type {PointerEvent} */ (event).pointerType;
    if (pointerType && pointerType !== 'mouse') return null;
    const target = event.target;
    return target instanceof Element && target.matches('product-card, .mv-card') ? target : null;
  };

  /** @param {Element} card */
  const start = (card) => {
    if (reducedMotion.matches || timers.has(card)) return;

    if (isHomeCard(card)) {
      const images = homeImages(card);
      if (images.length < 2) return;

      // Fetch the other photos now so the 2-second changes never flash empty.
      images.forEach((img) => {
        /** @type {HTMLImageElement} */ (img).loading = 'eager';
      });

      let current = 0;
      timers.set(
        card,
        window.setInterval(() => {
          current = (current + 1) % images.length;
          showHomeImage(card, current);
        }, INTERVAL_MS)
      );
      return;
    }

    const slideshow = slideshowOf(card);
    if (!slideshow || slideshow.getAttribute('disabled') === 'true') return;
    if ((slideshow.slides || []).length < 2) return;

    // Fetch the other photos now so the 2-second changes never flash white.
    card.querySelectorAll('.card-gallery img[loading="lazy"]').forEach((img) => {
      /** @type {HTMLImageElement} */ (img).loading = 'eager';
    });

    timers.set(
      card,
      window.setInterval(() => {
        const slides = slideshow.slides || [];
        if (slides.length < 2) return;
        slideshow.select((slideshow.current + 1) % slides.length, undefined, { animate: false });
      }, INTERVAL_MS)
    );
  };

  /** @param {Element} card */
  const stop = (card) => {
    const timer = timers.get(card);
    if (timer !== undefined) {
      window.clearInterval(timer);
      timers.delete(card);
    }

    if (isHomeCard(card)) {
      showHomeImage(card, 0);
      return;
    }

    const slideshow = slideshowOf(card);
    if (slideshow && slideshow.current > 0) slideshow.select(0, undefined, { animate: false });
  };

  // pointerenter/pointerleave don't bubble, but they can be caught while capturing, so one pair of
  // document listeners covers every card, including ones added later (pagination, filters, quick view).
  document.addEventListener(
    'pointerenter',
    (event) => {
      const card = cardFrom(event);
      if (card) start(card);
    },
    true
  );

  document.addEventListener(
    'pointerleave',
    (event) => {
      const card = cardFrom(event);
      if (card) stop(card);
    },
    true
  );
})();